<template>
  <div class="calico-date-picker">
    <div class="input-container">
      <input
        ref="dateInput"
        v-model="formattedDate"
        type="text"
        :placeholder="placeholder"
        maxlength="8"
        @blur="onBlur"
        @input="onInput"
      >
      <div class="datepicker-wrapper">
        <Datepicker
          ref="datepickerRef"
          v-model="selectedDate"
          :format="dateFormat"
          :clearable="false"
          :hide-input-icon="true"
          class="datepicker-inner"
        />
      </div>
      <i
        :class="endIcon"
        class="calico-end-icon"
        @click="openCalendar"
      />
    </div>
  </div>
</template>

<script>
import { ref, watch } from "vue";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

export default {
  name: "CalicoDatePicker",
  components: {
    Datepicker,
  },
  props: {
    placeholder: {
      type: String,
      default: "mm/dd/yyyy",
    },
    modelValue: {
      type: String,
      default: "",
    },
    dateFormat: {
      type: String,
      default: "mm/dd/yyyy",
    },
    endIcon: {
      type: String,
      default: "bi bi-calendar3",
    },
  },
  setup(props, { emit }) {
    const selectedDate = ref(null);
    const formattedDate = ref(props.modelValue);
    const datepickerRef = ref(null);

    const onBlur = () => {
      const input = formattedDate.value.trim();
      if (input.length === 8) {
        let month = parseInt(input.slice(0, 2), 10);
        let day = parseInt(input.slice(2, 4), 10);
        const year = parseInt(input.slice(4, 8), 10);

        if (month > 12) {
          [month, day] = [day, month];
        }

        month -= 1;

        const newDate = new Date(year, month, day);
        if (!isNaN(newDate.getTime())) {
          selectedDate.value = newDate;
          formattedDate.value = `${String(month + 1).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
          emit("update:modelValue", formattedDate.value);
        }
      }
    };

    const onInput = (event) => {
      formattedDate.value = event.target.value.replace(/[^0-9]/g, "");
    };

    const openCalendar = () => {
      if (datepickerRef.value) {
        datepickerRef.value.openMenu();
      }
    };

    watch(
      () => props.modelValue,
      (newValue) => {
        if (newValue !== formattedDate.value) {
          formattedDate.value = newValue;
          if (newValue) {
            const [month, day, year] = newValue.split('/');
            selectedDate.value = new Date(year, month - 1, day);
          } else {
            selectedDate.value = null;
          }
        }
      }
    );

    watch(selectedDate, (newValue) => {
      if (newValue) {
        const date = new Date(newValue);
        if (!isNaN(date.getTime())) {
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          const newFormattedDate = `${month}/${day}/${year}`;
          if (newFormattedDate !== formattedDate.value) {
            formattedDate.value = newFormattedDate;
            emit("update:modelValue", formattedDate.value);
          }
        }
      }
    });

    return {
      selectedDate,
      formattedDate,
      onBlur,
      onInput,
      datepickerRef,
      openCalendar,
    };
  },
};
</script>

<style lang="scss" scoped>
.calico-date-picker {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  .input-container {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
  }

  input {
    width: 100%;
    border-radius: $radius-small;
    padding: $space-small $space-medium;
    border: $border-width-default solid $color-neutral-light;
    @extend %label-medium;
    &:focus {
      outline: none;
    }
  }

  .datepicker-wrapper {
    @include stack-space($space-medium);
    justify-content: flex-end;
    align-items: center;
    position: absolute;
    height: 100%;
    width: 57px;
    right: 0;
    top: 15%;
    ::v-deep(.dp__outer_menu_wrap) {
      top: calc(100% + 8px) !important;
    }
    ::v-deep(.dp__arrow_bottom) {
      bottom: 100%;
      transform: translateY(50%) rotate(225deg);
    }
  }

  .datepicker-inner {
    ::v-deep .dp__input {
      width: 20px;
      height: 20px;
      opacity: 0;
      border: none;
      padding: 0;
      z-index: 9000;
    }
  }

  .calico-end-icon {
    position: absolute;
    right: 10.5px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    font-size: 24px;
  }
}
</style>
